import React, { useState, useEffect } from 'react';
import { 
  FaMobileAlt, 
  FaLock, 
  FaCheckCircle, 
  FaTimesCircle, 
  FaEye, 
  FaEyeSlash,
  FaShieldAlt,
  FaUserCog
} from 'react-icons/fa';
import { auth, database, ref, get, set } from '../firebase';
import { useNavigate } from 'react-router-dom';

const Setting = () => {
  const [formData, setFormData] = useState({
    mobile: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [errors, setErrors] = useState({});
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [passwordStrength, setPasswordStrength] = useState({
    level: 0,
    message: '',
    color: 'gray'
  });
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const currentUser = JSON.parse(localStorage.getItem('currentUser'));
        if (!currentUser || !currentUser.mobile) {
          setErrors({ general: 'User data not found. Please log in again.' });
          setMessage('User data not found. Please log in again.');
          setShowError(true);
          setIsLoading(false);
          navigate('/login');
          return;
        }

        const userRef = ref(database, `videoplayer/Users/${currentUser.mobile}`);
        const snapshot = await get(userRef);
        if (!snapshot.exists()) {
          setErrors({ general: 'User not found in database.' });
          setMessage('User not found in database.');
          setShowError(true);
          setIsLoading(false);
          navigate('/login');
          return;
        }

        setFormData((prev) => ({
          ...prev,
          mobile: currentUser.mobile,
        }));
      } catch (err) {
        console.error('Firebase Error:', err);
        setErrors({ general: 'Error fetching user data.' });
        setMessage('Error fetching user data. Please try again.');
        setShowError(true);
      } finally {
        setIsLoading(false);
      }
    };

    fetchUserData();
  }, [navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
    
    if (name === 'newPassword') {
      checkPasswordStrength(value);
    }
  };

  const checkPasswordStrength = (password) => {
    if (!password) {
      setPasswordStrength({
        level: 0,
        message: '',
        color: 'gray'
      });
      return;
    }
    
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumbers = /\d/.test(password);
    const hasSpecialChars = /[!@#$%^&*(),.?":{}|<>]/.test(password);
    
    let strength = 0;
    if (password.length > 5) strength++;
    if (password.length > 8) strength++;
    if (hasUpperCase && hasLowerCase) strength++;
    if (hasNumbers) strength++;
    if (hasSpecialChars) strength++;
    
    let color = '';
    let message = '';
    
    switch(strength) {
      case 0:
      case 1:
        color = 'red';
        message = 'Very Weak';
        break;
      case 2:
        color = 'orange';
        message = 'Weak';
        break;
      case 3:
        color = 'yellow';
        message = 'Moderate';
        break;
      case 4:
        color = 'blue';
        message = 'Strong';
        break;
      case 5:
        color = 'green';
        message = 'Very Strong';
        break;
      default:
        color = 'gray';
        message = '';
    }
    
    setPasswordStrength({
      level: strength,
      message,
      color
    });
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.newPassword) {
      newErrors.newPassword = 'New password is required';
    } else if (formData.newPassword.length < 6) {
      newErrors.newPassword = 'Password must be at least 6 characters';
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData.newPassword !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setShowError(false);
    setShowSuccess(false);

    if (!validateForm()) {
      setMessage('Please fix the errors in the form');
      setShowError(true);
      setIsLoading(false);
      return;
    }

    try {
      const userRef = ref(database, `videoplayer/Users/${formData.mobile}`);
      const snapshot = await get(userRef);
      if (!snapshot.exists()) {
        setErrors({ general: 'User not found in database.' });
        setMessage('User not found in database.');
        setShowError(true);
        setIsLoading(false);
        return;
      }

      const currentUserData = snapshot.val();
      
      await set(userRef, {
        ...currentUserData,
        password: formData.newPassword,
        confirmPassword: formData.confirmPassword,
        lastPasswordUpdate: new Date().toISOString(),
      });

      setMessage('Your password has been changed successfully! Please log in again with your new password.');
      setShowSuccess(true);
      setFormData({
        ...formData,
        newPassword: '',
        confirmPassword: '',
      });
      
      setTimeout(() => {
        localStorage.removeItem('currentUser');
        navigate('/login');
      }, 2000);
    } catch (err) {
      console.error('Firebase Error:', err);
      let errorMessage = 'An error occurred while updating your password.';
      if (err.code === 'permission-denied') {
        errorMessage = 'Permission denied. Please contact support.';
      } else if (err.message) {
        errorMessage = err.message;
      }
      setErrors({ general: errorMessage });
      setMessage(errorMessage);
      setShowError(true);
    } finally {
      setIsLoading(false);
    }
  };

  const closePopup = () => {
    setShowSuccess(false);
    setShowError(false);
    setErrors({});
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-purple-100 flex items-center justify-center">
        <div className="flex flex-col items-center">
          <FaUserCog className="h-16 w-16 text-indigo-600 mb-4" />
          <p className="text-lg font-medium text-gray-700">
            Loading your settings...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-purple-100 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md mx-auto bg-white rounded-2xl shadow-xl overflow-hidden md:max-w-2xl">
        <div className="p-8">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center bg-indigo-100 p-4 rounded-full mb-4">
              <FaShieldAlt className="h-8 w-8 text-indigo-600" />
            </div>
            <h2 className="text-3xl font-bold text-gray-900">Account Security</h2>
            <p className="mt-2 text-sm text-gray-600">
              Update your password to keep your account secure
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Mobile Number Field */}
            <div>
              <label htmlFor="mobile" className="block text-sm font-medium text-gray-700 mb-1">
                Registered Mobile
              </label>
              <div className="mt-1 relative rounded-lg shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FaMobileAlt className="h-5 w-5 text-indigo-400" />
                </div>
                <input
                  type="tel"
                  name="mobile"
                  id="mobile"
                  value={formData.mobile}
                  onChange={handleChange}
                  disabled
                  className="block w-full pl-10 pr-3 py-3 border-2 border-gray-200 rounded-lg bg-gray-50 text-gray-600 focus:outline-none sm:text-sm"
                  placeholder="+91 1234567890"
                />
              </div>
            </div>

            {/* New Password Field */}
            <div>
              <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 mb-1">
                New Password
              </label>
              <div className="mt-1 relative rounded-lg shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FaLock className={`h-5 w-5 ${
                    errors.newPassword ? 'text-red-400' : 'text-indigo-400'
                  }`} />
                </div>
                <input
                  type={showNewPassword ? 'text' : 'password'}
                  name="newPassword"
                  id="newPassword"
                  value={formData.newPassword}
                  onChange={handleChange}
                  className={`block w-full pl-10 pr-10 py-3 border-2 ${
                    errors.newPassword ? 'border-red-300' : 'border-gray-200'
                  } rounded-lg placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 sm:text-sm`}
                  placeholder="Enter your new password"
                />
                <div 
                  className="absolute inset-y-0 right-0 pr-3 flex items-center cursor-pointer" 
                  onClick={() => setShowNewPassword((prev) => !prev)}
                >
                  {showNewPassword ? 
                    <FaEyeSlash className="h-5 w-5 text-gray-500" /> : 
                    <FaEye className="h-5 w-5 text-gray-500" />
                  }
                </div>
              </div>
              
              {/* Password Strength Indicator */}
              {formData.newPassword && (
                <div className="mt-2 space-y-1">
                  <div className="flex items-center space-x-2">
                    <div className="flex-1 bg-gray-200 rounded-full h-2 overflow-hidden">
                      <div 
                        className={`h-full rounded-full ${
                          passwordStrength.color === 'red' ? 'bg-red-500' :
                          passwordStrength.color === 'orange' ? 'bg-orange-500' :
                          passwordStrength.color === 'yellow' ? 'bg-yellow-500' :
                          passwordStrength.color === 'blue' ? 'bg-blue-500' :
                          'bg-green-500'
                        }`}
                        style={{ width: `${(passwordStrength.level / 5) * 100}%` }}
                      />
                    </div>
                    <span className={`text-xs font-medium ${
                          passwordStrength.color === 'red' ? 'text-red-600' :
                          passwordStrength.color === 'orange' ? 'text-orange-600' :
                          passwordStrength.color === 'yellow' ? 'text-yellow-600' :
                          passwordStrength.color === 'blue' ? 'text-blue-600' :
                          'text-green-600'
                        }`}>
                      {passwordStrength.message}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500">
                    {passwordStrength.level >= 4 ? 'Strong password!' : 
                     'Tip: Use uppercase, numbers & special characters'}
                  </p>
                </div>
              )}
              
              {errors.newPassword && (
                <p className="mt-1 text-sm text-red-600">
                  {errors.newPassword}
                </p>
              )}
            </div>

            {/* Confirm Password Field */}
            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
                Confirm Password
              </label>
              <div className="mt-1 relative rounded-lg shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FaLock className={`h-5 w-5 ${
                    errors.confirmPassword ? 'text-red-400' : 'text-indigo-400'
                  }`} />
                </div>
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  name="confirmPassword"
                  id="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  className={`block w-full pl-10 pr-10 py-3 border-2 ${
                    errors.confirmPassword ? 'border-red-300' : 'border-gray-200'
                  } rounded-lg placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 sm:text-sm`}
                  placeholder="Confirm your password"
                />
                <div 
                  className="absolute inset-y-0 right-0 pr-3 flex items-center cursor-pointer" 
                  onClick={() => setShowConfirmPassword((prev) => !prev)}
                >
                  {showConfirmPassword ? 
                    <FaEyeSlash className="h-5 w-5 text-gray-500" /> : 
                    <FaEye className="h-5 w-5 text-gray-500" />
                  }
                </div>
              </div>
              {errors.confirmPassword && (
                <p className="mt-1 text-sm text-red-600">
                  {errors.confirmPassword}
                </p>
              )}
            </div>

            {/* Submit Button */}
            <div>
              <button
                type="submit"
                disabled={isLoading}
                className={`w-full flex justify-center items-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white ${
                  isLoading ? 'bg-indigo-400 cursor-not-allowed' : 'bg-indigo-600 hover:bg-indigo-700'
                } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500`}
              >
                {isLoading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Updating...
                  </>
                ) : (
                  <>
                    <FaLock className="mr-2" />
                    Update Password
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Success Popup */}
      {showSuccess && (
        <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
          <div className="absolute inset-0 bg-black bg-opacity-50"></div>
          <div className="bg-white rounded-xl p-6 z-10 max-w-sm w-full mx-4 shadow-2xl">
            <div className="flex flex-col items-center">
              <FaCheckCircle className="h-14 w-14 text-green-500 mb-4" />
              <h3 className="text-xl font-bold text-gray-900 mb-2">Success!</h3>
              <p className="text-sm text-gray-600 text-center mb-6">{message}</p>
              <button
                onClick={closePopup}
                className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Error Popup */}
      {showError && (
        <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
          <div className="absolute inset-0 bg-black bg-opacity-50"></div>
          <div className="bg-white rounded-xl p-6 z-10 max-w-sm w-full mx-4 shadow-2xl">
            <div className="flex flex-col items-center">
              <FaTimesCircle className="h-14 w-14 text-red-500 mb-4" />
              <h3 className="text-xl font-bold text-gray-900 mb-2">Oops!</h3>
              <p className="text-sm text-gray-600 text-center mb-6">{message}</p>
              <button
                onClick={closePopup}
                className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Setting;